import { createServer, createConnection, type Server } from 'node:net';
import { spawn } from 'node:child_process';
import { existsSync, mkdirSync, unlinkSync, readdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { forComponent } from '../src/index';
import type { AgentTool } from '../src/tools';
import { globalSessionLoad } from './session';

const log = forComponent('remote-trigger');

/**
 * RemoteTrigger — invoke ANOTHER agentx session on demand (the event-driven sibling of the
 * scheduler's "run X at time T"). Two local tiers, tried in order:
 *
 *   1. LIVE session (same machine): every REPL listens on a per-session unix socket under
 *      ~/.agent/triggers/ (same-user only — the dir is 0700). A trigger lands as an injected
 *      prompt in the OPEN terminal (same seam as a scheduler fire), so there is no second
 *      writer racing the session JSON. The caller gets an ack, not the result — the result
 *      belongs to the live session.
 *   2. HEADLESS spawn: no live socket → `agentx -p "<prompt>" --resume <id> --yes` from the
 *      target session's own cwd (resolved via the global session index). The caller gets the
 *      run's final text.
 *
 * Cross-machine triggering is deliberately NOT here — that's the MCP-server direction
 * (mind/13): an agentx exposed as a connectable MCP with real auth.
 */

const TRIGGER_DIR = () => join(homedir(), '.agent', 'triggers');
const sockPath = (sessionId: string, dir = TRIGGER_DIR()) => join(dir, `${sessionId}.sock`);

export interface TriggerRequest { prompt: string; from?: string }

/** Per-REPL listener: accepts {prompt} JSON lines and injects them into the live session. */
export class TriggerServer {
  private server?: Server;
  private path?: string;

  constructor(private onTrigger: (req: TriggerRequest) => void) {}

  /** Start listening for this session. Never throws — a failed listener just disables tier 1. */
  start(sessionId: string, dir = TRIGGER_DIR()): void {
    this.stop();
    try {
      mkdirSync(dir, { recursive: true, mode: 0o700 });
      const p = sockPath(sessionId, dir);
      try { unlinkSync(p); } catch { /* no stale socket */ }
      this.server = createServer((conn) => {
        let buf = '';
        conn.on('data', (d) => {
          buf += d.toString();
          const nl = buf.indexOf('\n');
          if (nl < 0) return;
          const line = buf.slice(0, nl);
          buf = buf.slice(nl + 1);
          try {
            const req = JSON.parse(line) as TriggerRequest;
            if (typeof req.prompt === 'string' && req.prompt.trim()) {
              this.onTrigger(req);
              conn.end(JSON.stringify({ ok: true, mode: 'live' }) + '\n');
            } else conn.end(JSON.stringify({ ok: false, error: 'empty prompt' }) + '\n');
          } catch (e) { conn.end(JSON.stringify({ ok: false, error: String(e) }) + '\n'); }
        });
        conn.on('error', (e) => log.debug('trigger conn error', e));
      });
      this.server.on('error', (e) => log.debug('trigger server error', e));
      this.server.listen(p);
      this.path = p;
    } catch (e) { log.debug('trigger server unavailable', e); }
  }

  /** Re-bind on /resume (the session id changed). */
  use(sessionId: string, dir = TRIGGER_DIR()): void { this.start(sessionId, dir); }

  stop(): void {
    this.server?.close();
    this.server = undefined;
    if (this.path) { try { unlinkSync(this.path); } catch { /* gone */ } this.path = undefined; }
  }
}

/** Tier 1: send {prompt} to a live session's socket. Resolves null when no live listener answers. */
export function triggerLive(sessionId: string, req: TriggerRequest, dir = TRIGGER_DIR(), timeoutMs = 3000): Promise<{ ok: boolean } | null> {
  const p = sockPath(sessionId, dir);
  if (!existsSync(p)) return Promise.resolve(null);
  return new Promise((res) => {
    const conn = createConnection(p);
    const done = (v: { ok: boolean } | null) => { conn.destroy(); res(v); };
    const timer = setTimeout(() => done(null), timeoutMs);
    let buf = '';
    conn.on('connect', () => conn.write(JSON.stringify(req) + '\n'));
    conn.on('data', (d) => {
      buf += d.toString();
      if (!buf.includes('\n')) return;
      clearTimeout(timer);
      try { done(JSON.parse(buf.slice(0, buf.indexOf('\n')))); } catch { done(null); }
    });
    conn.on('error', () => { clearTimeout(timer); try { unlinkSync(p); } catch { /* held elsewhere */ } done(null); }); // stale socket → clean + fall through
  });
}

/** Live sessions visible on this machine (sockets present under the trigger dir). */
export function liveSessions(dir = TRIGGER_DIR()): string[] {
  if (!existsSync(dir)) return [];
  return readdirSync(dir).filter((f) => f.endsWith('.sock')).map((f) => f.slice(0, -5));
}

export interface RemoteTriggerOptions {
  /** RAW shell-ready interpreter+script (same convention as OsSchedulerOptions.agentx). */
  agentx: string;
  /** This session's id — triggering yourself is rejected (it would deadlock the live seam). */
  selfId?: () => string;
  /** Injectable for tests. */
  spawnFn?: typeof spawn;
  triggerDir?: string;
  /** Headless run hard cap. */
  timeoutMs?: number;
}

/** The RemoteTrigger agent tool: live-socket first, headless `--resume` spawn as fallback. */
export function makeRemoteTriggerTool(opts: RemoteTriggerOptions): AgentTool {
  const dir = opts.triggerDir ?? TRIGGER_DIR();
  const doSpawn = opts.spawnFn ?? spawn;
  return {
    name: 'RemoteTrigger',
    description:
      'Trigger ANOTHER agentx session (this machine) with a prompt. A session open in a live terminal receives it ' +
      'as an injected turn (you get an ack; the result shows there). Otherwise the session is resumed headless and ' +
      'you get its final answer. Find ids via the user or `ls ~/.agent/sessions`. Not for self-delegation — use Task for that.',
    parameters: {
      type: 'object',
      required: ['sessionId', 'prompt'],
      properties: {
        sessionId: { type: 'string', description: 'target session id (or unique prefix/suffix)' },
        prompt: { type: 'string', description: 'the prompt to run in that session' },
      },
    },
    async run({ sessionId, prompt }) {
      const id = String(sessionId ?? '').trim();
      const text = String(prompt ?? '').trim();
      if (!id || !text) return 'Error: sessionId and prompt are required.';
      const target = globalSessionLoad(id);
      if (!target) return `Error: no session matching '${id}' in the global index (~/.agent/sessions).`;
      const full = target.meta.id;
      if (opts.selfId && full === opts.selfId()) return 'Error: that is THIS session — use Task/TaskBatch for self-delegation.';
      // Tier 1: live terminal on this machine
      const live = await triggerLive(full, { prompt: text, from: opts.selfId?.() }, dir);
      if (live?.ok) return `Delivered to live session ${full} — it runs there; the user sees it in that terminal.`;
      // Tier 2: headless resume from the target's own project dir
      if (!target.meta.cwd || !existsSync(target.meta.cwd)) return `Error: session ${full} has no reachable cwd (${target.meta.cwd ?? 'unknown'}).`;
      return new Promise<string>((res) => {
        const child = doSpawn('/bin/sh', ['-c', `${opts.agentx} -p "$1" --resume "$2" --yes`, 'sh', text, full], {
          cwd: target.meta.cwd, stdio: ['ignore', 'pipe', 'pipe'],
        });
        let out = '', errOut = '';
        child.stdout?.on('data', (d) => { out += d; });
        child.stderr?.on('data', (d) => { errOut += d; });
        const killer = setTimeout(() => { child.kill(); }, opts.timeoutMs ?? 10 * 60_000);
        child.on('close', (code) => {
          clearTimeout(killer);
          const answer = out.trim().slice(-8000);
          res(code === 0
            ? `Headless run of ${full} finished:\n${answer || '(no output)'}`
            : `Error: headless run of ${full} exited ${code}: ${(errOut || out).trim().slice(-1000)}`);
        });
        child.on('error', (e) => { clearTimeout(killer); res(`Error: could not spawn agentx: ${e.message}`); });
      });
    },
  };
}
